import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { finalists } from "@/mocks/finalists";
import { categories } from "@/mocks/categories";
import { rankingHighlights, rankingTrend } from "@/mocks/ranking";

type RankItem = (typeof finalists)[number];

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line",
  down: "ri-arrow-down-line",
  steady: "ri-subtract-line",
};

const trendStyle: Record<string, string> = {
  up: "text-primary-600 bg-primary-50",
  down: "text-secondary-600 bg-secondary-50",
  steady: "text-foreground-500 bg-background-100",
};

export default function RankingBoard() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [items, setItems] = useState<RankItem[]>(() => [...finalists].sort((a, b) => b.score - a.score));
  const [updatedAt, setUpdatedAt] = useState("刚刚");

  const rows = useMemo(
    () => items.filter((item) => activeCategory === "all" || item.category === activeCategory),
    [items, activeCategory],
  );

  const handleRefresh = () => {
    setItems((prev) =>
      [...prev]
        .map((item) => ({
          ...item,
          score: Math.min(10, Math.max(6.5, Number((item.score + (Math.random() - 0.45) * 0.08).toFixed(1)))),
        }))
        .sort((a, b) => b.score - a.score),
    );
    setUpdatedAt(new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" }));
  };

  return (
    <section className="bg-background-50 pb-16 md:pb-24">
      <div className="w-full px-4 md:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 my-8">
            {rankingHighlights.map((highlight) => (
              <div key={highlight.label} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="flex items-center gap-2 text-xs font-bold tracking-wide text-primary-600">
                  <i className={highlight.icon} />
                  {highlight.label}
                </span>
                <p className="mt-3 font-heading text-lg tracking-wide text-foreground-950">{highlight.value}</p>
                <p className="mt-1 text-sm text-foreground-600">{highlight.desc}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl md:text-2xl tracking-wide text-foreground-950">
                  群众打分 7 日走势
                </h2>
                <p className="mt-1 text-sm text-foreground-600">展示当前排名前三作品过去一周的平均得分变化。</p>
              </div>
              <span className="flex items-center gap-2 text-xs text-foreground-500">
                <span className="w-2 h-2 rounded-full bg-primary-500" />
                第一名
                <span className="w-2 h-2 rounded-full bg-accent-500 ml-2" />
                第二名
                <span className="w-2 h-2 rounded-full bg-secondary-500 ml-2" />
                第三名
              </span>
            </div>

            <div className="mt-6 w-full h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rankingTrend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 12, fill: "oklch(var(--foreground-500))" }}
                    axisLine={{ stroke: "oklch(var(--background-300))" }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[7, 10]}
                    tick={{ fontSize: 12, fill: "oklch(var(--foreground-500))" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid oklch(var(--background-300))",
                      fontSize: 12,
                      background: "oklch(var(--background-50))",
                    }}
                    labelStyle={{ color: "oklch(var(--foreground-800))" }}
                  />
                  <Line type="monotone" dataKey="top1" stroke="oklch(var(--primary-500))" strokeWidth={2.5} dot={false} name="第一名" />
                  <Line type="monotone" dataKey="top2" stroke="oklch(var(--accent-500))" strokeWidth={2.5} dot={false} name="第二名" />
                  <Line type="monotone" dataKey="top3" stroke="oklch(var(--secondary-500))" strokeWidth={2.5} dot={false} name="第三名" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap gap-1.5 p-1 rounded-full bg-background-100 w-fit">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === cat.id
                      ? "bg-foreground-900 text-background-50 font-medium"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-500">更新于 {updatedAt}</span>
              <button
                type="button"
                onClick={handleRefresh}
                className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" />
                刷新排名
              </button>
            </div>
          </div>

          <div className="mt-6 overflow-x-auto rounded-lg border border-background-200">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <caption className="sr-only">爆料大赛群众打分排名结果</caption>
              <thead>
                <tr className="bg-background-100 text-foreground-600 text-xs">
                  <th scope="col" className="text-left font-medium px-4 py-3.5 w-20">排名</th>
                  <th scope="col" className="text-left font-medium px-4 py-3.5">入围作品</th>
                  <th scope="col" className="text-left font-medium px-4 py-3.5 w-32">分类</th>
                  <th scope="col" className="text-right font-medium px-4 py-3.5 w-28">群众打分</th>
                  <th scope="col" className="text-right font-medium px-4 py-3.5 w-28">投票数</th>
                  <th scope="col" className="text-right font-medium px-4 py-3.5 w-24">变化</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {rows.map((item) => {
                  const rank = items.findIndex((i) => i.id === item.id) + 1;
                  return (
                    <tr key={item.id} className="hover:bg-background-100/60 transition-colors">
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex w-8 h-8 rounded-md items-center justify-center font-heading text-base ${
                            rank <= 3 ? "bg-accent-500 text-accent-950" : "bg-background-100 text-foreground-600"
                          }`}
                        >
                          {rank}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <p className="font-medium text-foreground-950">{item.title}</p>
                        <p className="mt-0.5 text-xs text-foreground-500">{item.author}</p>
                      </td>
                      <td className="px-4 py-4">
                        <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs whitespace-nowrap">
                          {item.categoryName}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right font-heading text-lg text-primary-600">
                        {item.score.toFixed(1)}
                      </td>
                      <td className="px-4 py-4 text-right text-foreground-700">{item.votes.toLocaleString("zh-CN")}</td>
                      <td className="px-4 py-4 text-right">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${trendStyle[item.trend]}`}
                        >
                          <i className={trendIcon[item.trend]} />
                          {item.rankChange === 0 ? "持平" : Math.abs(item.rankChange)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-foreground-500">
            注：群众打分采用去掉最高分与最低分后取平均的方式计算，榜单数据随投票持续变化，页面展示的是当前最新结果。
          </p>
        </div>
      </div>
    </section>
  );
}